// SelectWorkspaceFlow — the workspace half of the switch layer (contract 07 §8, ORP-18636). Ports
// v3 `BootstrapV2Flow.executeWorkspaceSwitch` (`:129-161`) together with the access guard that
// lived in `workspaceStore.setSelectedWorkspace` (`:262-267`). Unlike v3 it:
//   • NEVER auto-logs-out on a switch error (v3 `:159` ended every unclassified failure in
//     `AuthLogoutFlow`). It classifies by `ApiError.kind`/`code` — never English prose (ledger A2)
//     — and RETURNS the outcome; the caller owns the copy (i18n belongs in the component).
//   • NEVER touches `session.status`: a switch is not a re-boot, so there is no splash and the
//     routed surface remounts once via `<RouterView :key="workspaceId">` (contract §7, ledger A5).
//     `sessionStore` remains the single writer of the state machine — it is not even imported here.
//   • Runs the access check FIRST, so a target the user cannot reach mutates NOTHING (v3 committed
//     `workspace` at `:135`, before its same-id check, and its guard lived in the store).
//   • Reaches into no other feature's store: workspace-scoped state is cleared through the
//     registered hydrator resets (v3 hard-coded `customizeStore.resetState()`, an island violation).
//
// `finalize`/`computeOnlyDashboard`/`hydrateWorkspaceLevel` are the shared `flowToolkit` (internal to
// `flows/`), which all three flows now consume — one copy, not three.
import type { BootstrapSection } from '../models/bootstrap.types'
import type { WorkspaceItem } from '../models/workspace.types'
import { BootstrapService } from '../service/BootstrapService'
import type { PackageSignal } from '../service/PackageService'
import { workspacePreferencesService } from '../service/WorkspacePreferencesService'
import { useAccountStore, type AccountStore } from '../stores/accountStore'
import { useWorkspaceStore, type WorkspaceStore } from '../stores/workspaceStore'

import { notifyContextSwitched } from './contextSwitchRegistry'
import {
  COMPOSITIONS_SECTIONS,
  FlowToolkit,
  toApiError,
  WORKSPACE_LEVEL_SECTIONS,
} from './flowToolkit'
import { dispatchBootstrap, resetBootstrapSections } from './hydratorRegistry'

import { useAuthStore, type AuthStore } from '@/features/auth'
import type { ApiError } from '@/shared/http'

/** The outcome of a workspace select. Returned rather than toasted so the flow stays headless and
 *  copy stays in the component (contract §13: zero hardcoded copy, and no i18n below the view).
 *  `failed` carries the CLASSIFIED `ApiError` (`kind`/`code`/`i18nKey`) — never a prose match. */
export type WorkspaceSwitchOutcome =
  | { status: 'noAccess' }
  | { status: 'unchanged' }
  | { status: 'switched' }
  | { status: 'failed'; error: ApiError }

export interface SelectWorkspaceHooks {
  /** Pricing / limit / onboarding SIGNAL raised by `finalize` — the modal itself is a credits /
   *  account UI concern and the app stays usable either way (contract §14; mirrors the boot flow). */
  onPackageSignal?: (signal: PackageSignal) => void
}

export interface SelectWorkspaceFlowOptions {
  bootstrapService?: BootstrapService
  accountStore?: AccountStore
  workspaceStore?: WorkspaceStore
  authStore?: AuthStore
  dispatch?: typeof dispatchBootstrap
  resetSections?: typeof resetBootstrapSections
  hooks?: SelectWorkspaceHooks
}

// The scope a switch RESETS before it re-hydrates. `bootWorkspaceSwitch` asks for the workspace
// sections plus `compositions` (v3's `WORKSPACE_SWITCH_SECTIONS`), so compositions is reset with the
// rest and dispatched EAGERLY whenever the switch response carries it — the boot flow's split
// (deferred compositions) is a COLD-BOOT optimisation and deliberately does not apply to a switch.
const WORKSPACE_SCOPED_SECTIONS: readonly BootstrapSection[] = [
  ...WORKSPACE_LEVEL_SECTIONS,
  ...COMPOSITIONS_SECTIONS,
]

export class SelectWorkspaceFlow {
  private readonly bootstrapService: BootstrapService
  private readonly accountStore: AccountStore
  private readonly workspaceStore: WorkspaceStore
  private readonly authStore: AuthStore
  private readonly resetSections: typeof resetBootstrapSections
  /** The hydrate / finalize toolkit shared with the boot + account-switch flows. Its `hooks` is THIS
   *  flow's object by reference, so a late-registered `onPackageSignal` still fires. */
  private readonly toolkit: FlowToolkit

  constructor(options: SelectWorkspaceFlowOptions = {}) {
    this.bootstrapService = options.bootstrapService ?? new BootstrapService()
    this.accountStore = options.accountStore ?? useAccountStore()
    this.workspaceStore = options.workspaceStore ?? useWorkspaceStore()
    this.authStore = options.authStore ?? useAuthStore()
    this.resetSections = options.resetSections ?? resetBootstrapSections
    this.toolkit = new FlowToolkit({
      accountStore: this.accountStore,
      workspaceStore: this.workspaceStore,
      dispatch: options.dispatch ?? dispatchBootstrap,
      hooks: options.hooks ?? {},
    })
  }

  /**
   * The workspace-select use case. `session.status` never changes — a switch is not a re-boot.
   *   • no id / no access → early return, ZERO state mutation, ZERO network (v3 `:262-267`).
   *   • same id → `finalize()` only, ZERO network (v3 `:141`).
   *   • otherwise → cancel in-flight → commit → reset workspace scope → `bootWorkspaceSwitch` →
   *     hydrate → `finalize()`.
   */
  public async execute(ws: WorkspaceItem | null): Promise<WorkspaceSwitchOutcome> {
    // v3's access predicate rejected an id-less target identically (`workspace_id === undefined`
    // matches nothing and `accountId !== undefined`), so this guard is that branch, made explicit.
    if (!ws?.id) return { status: 'noAccess' }
    if (!this.canAccess(ws)) return { status: 'noAccess' }
    if (this.workspaceStore.workspaceId === ws.id) {
      this.finalize()
      return { status: 'unchanged' }
    }
    return this.switchTo(ws)
  }

  /**
   * The unconditional switch. Public because `SelectAccountFlow` runs exactly this as its second
   * half: v3's `executeAccountSwitch` (`:104-114`) re-picks a workspace and switches to it WITHOUT
   * the access/same-id checks — those guard a USER-initiated pick, not a re-pick made from the
   * freshly-hydrated list of the account we just switched to.
   */
  public async switchTo(ws: WorkspaceItem): Promise<WorkspaceSwitchOutcome> {
    // v3 order preserved: commit, then cancel + reset, then fetch (`:135-148`). `resetState(true)`
    // keeps the list/selection/cache, so committing first is safe.
    this.commit(ws)
    this.resetWorkspaceScope()

    try {
      const result = await this.bootstrapService.bootWorkspaceSwitch(
        Number(this.accountStore.accountId),
        ws.id,
        { refresh: true },
      )
      this.toolkit.hydrateWorkspaceLevel(result)
    } catch (error) {
      // Classified, reported, NOT logged out (v3 `:157-159`) and NOT an `error` session status:
      // the shell stays mounted on the (already committed) workspace and the caller surfaces it.
      return { status: 'failed', error: toApiError(error) }
    }

    this.finalize()
    notifyContextSwitched()
    return { status: 'switched' }
  }

  /**
   * Cancel in-flight work and drop everything scoped to the workspace we are leaving.
   *
   * Public because an ACCOUNT switch into an account with zero workspaces has no `switchTo` to run
   * (contract 07 §8) and previously finalised with the old workspace's requests still in flight and
   * its bootstrap sections still hydrated. That is exactly the state that now lands the user in the
   * onboarding wizard, so the teardown has to happen on both paths — one copy, not two.
   */
  public resetWorkspaceScope(): void {
    this.authStore.abortAll() // auth's cancelAllTokens (06) — v3 `:142`
    this.workspaceStore.resetState(true) // keepList: list + selection + cache survive (§7)
    this.accountStore.clearRbacCapabilities()
    workspacePreferencesService.clear()
    this.resetSections(WORKSPACE_SCOPED_SECTIONS)
  }

  /**
   * Recompute permissions + package + the dashboards-only flag + the package signal (v1
   * `applyUserPackages`, `AccountBootstrapFlow.ts:87-138`) — see `flowToolkit.finalize`.
   * Load-bearing on a workspace switch: `workspace_permissions[wid].permissions` OVERRIDE the
   * account's, so the effective RBAC changes with the workspace. Stays PUBLIC because
   * `SelectAccountFlow` calls it directly for an account with no workspace at all (ORP-18636
   * "shares finalize") — that delegation is this class's surface, not the toolkit's.
   */
  public finalize(): void {
    this.toolkit.finalize()
  }

  /** v3 `workspaceStore.ts:262-267`, preserved EXACTLY (incl. its strict id comparison): allowed
   *  when the account detail grants the workspace explicitly, OR no account is selected yet, OR the
   *  workspace belongs to the selected account. Anything else → denial, with no mutation. */
  private canAccess(ws: WorkspaceItem): boolean {
    const granted = this.accountStore.accountDetail?.workspace_permissions?.some(
      (permission) => permission.workspace_id === ws.id,
    )
    if (granted) return true
    const accountId = this.accountStore.accountId
    if (!accountId) return true
    return accountId === ws.account_id
  }

  private commit(ws: WorkspaceItem): void {
    this.workspaceStore.setSelectedWorkspace(ws)
    const accountId = this.accountStore.accountId
    if (accountId !== undefined) this.workspaceStore.setOldWorkspaceForAccount(accountId, ws.id)
  }
}
